import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Divider,
  Drawer,
  Dropdown,
  Grid,
  Select,
  Space,
  Tooltip,
  Typography,
} from "antd";
import {
  CustomerFilesDrawer,
  CustomerInfo,
  FileUploadModal,
} from "../customer";
import QuickbooksContext from "../../context/QuickbooksContext";
import { useDemoModal } from "../../hooks";
import { AlertComponent } from "../ui/AlertComponent";
import DataContext from "../../context/DataContext";
import { USDollar } from "../../utils/func/currency";
import { CustomerProps, ProjectProps } from "../../interfaces/interfaces";
import {
  PlusOutlined,
  UploadOutlined,
  FileOutlined,
  FolderOpenOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { DrawerCreateOrUpdateProject, ProjectList } from "../project";
import { MODAL_INITIAL_STATE, PROJECT_INITIAL_STATE } from "../../utils/data";
import AuthContext from "../../context/AuthContext";
import { MenuProps } from "antd/lib";
import { useNavigate } from "react-router-dom";
import { filterOption } from "../../utils/func/utils";
const { Text } = Typography;

interface Props {
  open: boolean;
  idClient: string;
  idCustomer: string | null;
  onSelectOne: (record: CustomerProps) => void;
  onClose: () => void;
  projectPreselected?: string | null;
}

export const CustomerDrawer = ({
  open,
  idClient,
  idCustomer,
  onSelectOne,
  onClose,
  projectPreselected,
}: Props) => {
  const navigate = useNavigate();
  const { useBreakpoint } = Grid;
  const { xs } = useBreakpoint();
  const { DemoModal } = useDemoModal();
  const { getCustomerData, alertData, qbLoading, customerInfo } =
    useContext(QuickbooksContext);
  const {
    projectSelected,
    projectListPaginated,
    getData,
    handleChangeProjectSelected,
    handleEditData,
    handleCreateData,
  } = useContext(DataContext);
  const { getClientInformation } = useContext(AuthContext);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [modalSelectProject, setModalSelectProject] = useState(false);
  const [fileDrawerVisible, setFileDrawerVisible] = useState(false);
  const [isModalFileUploadVisible, setIsModalFileUploadVisible] =
    useState(false);

  const [projectDrawerStatus, setProjectDrawerStatus] = useState<{
    open: boolean;
    create: boolean;
    update: boolean;
    payload: ProjectProps | null;
  }>({
    open: false,
    create: false,
    update: false,
    payload: null,
  });

  useEffect(() => {
    idClient &&
      idCustomer &&
      getCustomerData(idCustomer, idClient, () => setOpenDrawer(true));
    getData("projects-paginated", idClient, undefined, {
      customer: idCustomer,
      active: true,
    });
  }, [idCustomer]);

  useEffect(() => {
    if (projectPreselected && !projectSelected) {
      handleChangeProjectSelected(projectPreselected);
    } else if (!projectSelected) {
      let selected = customerInfo?.Projects?.[0]?.id?._id;
      selected && handleChangeProjectSelected(selected);
    } else {
      !customerInfo?.Projects?.map((project) => project.id?._id).includes(
        projectSelected,
      ) && handleChangeProjectSelected(null);
    }
  }, [projectPreselected, projectSelected, customerInfo]);

  useEffect(() => {
    customerInfo.Projects?.length === 0 && handleChangeProjectSelected(null);
  }, [customerInfo]);

  const updateProject = (
    values: ProjectProps,
    idClient: string,
    idProject?: string,
  ) => {
    if (projectDrawerStatus?.update && idProject) {
      handleEditData({
        endpoint: `project/${idClient}/${idProject}`,
        newData: values,
        next: () => {
          idClient && idProject && getData("project-templates", idClient);
          idClient &&
            idCustomer &&
            getCustomerData(idCustomer, idClient, () => {});

          getData("projects-paginated", idClient, undefined, {
            customer: idCustomer,
            active: true,
          });
          handleChangeProjectSelected(projectSelected);
        },
      });
      setProjectDrawerStatus(MODAL_INITIAL_STATE);
    } else if (projectDrawerStatus.create) {
      handleCreateData({
        endpoint: "projects",
        idClient,
        data: values,
        next: () => {
          idClient &&
            idCustomer &&
            getCustomerData(idCustomer, idClient, () => {});

          getData("projects-paginated", idClient, undefined, {
            customer: idCustomer,
            active: true,
          });
        },
      });
      setProjectDrawerStatus(MODAL_INITIAL_STATE);
    }
  };

  const items: MenuProps["items"] = [
    {
      label: "Create New Project",
      key: "1",
      onClick: () => {
        setProjectDrawerStatus({
          open: true,
          create: true,
          update: false,
          payload: {
            ...PROJECT_INITIAL_STATE,
            customers: [customerInfo],
          },
        });
      },
    },
    {
      label: "Select Existing Project",
      key: "2",
      onClick: () => setModalSelectProject(true),
    },
  ];

  return (
    <>
      <Drawer
        zIndex={998}
        title={
          <Space
            direction="horizontal"
            style={{
              flexWrap: "wrap",
              rowGap: 8,
              columnGap: 16,
              width: "100%",
              alignItems: "flex-start",
            }}
          >
            <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
              <Space
                direction="horizontal"
                size={0}
                style={{ flexWrap: "wrap", minWidth: 0 }}
              >
                <Text
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 180,
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                  title={customerInfo.DisplayName}
                >
                  {customerInfo.DisplayName}
                </Text>
                <Divider type="vertical" />
                <Tooltip title="Open Client Details" placement="top">
                  <Button
                    type="link"
                    onClick={() =>
                      navigate(
                        `/customer-detail/${idClient}/${customerInfo._id}`,
                      )
                    }
                    size="small"
                    icon={<FolderOpenOutlined />}
                  ></Button>
                </Tooltip>
              </Space>
              <Text>
                Balance: {USDollar.format(customerInfo?.Balance) || 0}
              </Text>

              {customerInfo?.PrimaryEmailAddr?.Address && (
                <Space
                  direction="horizontal"
                  size={0}
                  style={{ flexWrap: "wrap" }}
                >
                  <Text
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 180,
                      display: "inline-block",
                      verticalAlign: "middle",
                    }}
                    title={customerInfo?.PrimaryEmailAddr?.Address}
                  >
                    {customerInfo?.PrimaryEmailAddr?.Address || ""}
                  </Text>
                  {" / "}
                  <Text
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 120,
                      display: "inline-block",
                      verticalAlign: "middle",
                    }}
                    title={customerInfo?.PrimaryPhone.FreeFormNumber}
                  >
                    {customerInfo?.PrimaryPhone.FreeFormNumber || ""}
                  </Text>
                </Space>
              )}
            </Space>
          </Space>
        }
        width={"100vw"}
        placement="top"
        height={"100vh"}
        styles={{
          body: {
            padding: 0,
          },
        }}
        onClose={() => {
          onClose();
          setOpenDrawer(false);
        }}
        maskClosable={false}
        open={openDrawer}
        loading={qbLoading}
        extra={[
          <Space wrap>
            <>
              {!xs && (
                <>
                  {customerInfo.parent?.Id ? (
                    <>
                      Related Customers:
                      <Button
                        type="link"
                        onClick={() =>
                          customerInfo.parent &&
                          onSelectOne(customerInfo.parent)
                        }
                      >
                        {customerInfo.parent.DisplayName}
                      </Button>
                    </>
                  ) : null}
                  {customerInfo?.children &&
                  customerInfo?.children?.length > 0 ? (
                    <>
                      Related Customers:
                      <Select
                        filterOption={filterOption}
                        showSearch
                        placeholder="Relationships"
                        style={{ width: 200, minWidth: 120 }}
                        onChange={(value) => {
                          onSelectOne(
                            customerInfo.children?.find(
                              (child) => child.Id === value,
                            ) as CustomerProps,
                          );
                        }}
                        options={customerInfo.children?.map((child) => ({
                          label: child.DisplayName,
                          value: child.Id,
                        }))}
                      ></Select>
                    </>
                  ) : null}
                </>
              )}
            </>

            <Select
              filterOption={filterOption}
              showSearch
              style={{ width: xs ? 120 : 200, minWidth: 120 }}
              disabled={!!projectPreselected}
              placeholder="Project"
              options={customerInfo.Projects?.map((project) => ({
                label: project?.id?.name,
                value: project?.id?._id,
              }))}
              value={projectSelected}
              onChange={(value) => {
                handleChangeProjectSelected(value);
                getData("activities-paginated", idClient, undefined, {
                  project: value,
                  customer: customerInfo._id,
                });
                getData("tasks", idClient, undefined, {
                  project: value,
                  customer: customerInfo._id,
                });
              }}
            ></Select>
          </Space>,
          xs ? (
            <></>
          ) : (
            <>
              <Dropdown menu={{ items }}>
                <Tooltip title="New Project" placement="left">
                  <Button icon={<PlusOutlined />}></Button>
                </Tooltip>
              </Dropdown>
              <Tooltip title="Edit Project">
                <Button
                  icon={<EditOutlined />}
                  style={{ marginLeft: "2px" }}
                  onClick={() => {
                    setProjectDrawerStatus({
                      open: true,
                      create: false,
                      update: true,
                      payload: {
                        ...(projectListPaginated?.data.find(
                          (project) => project?._id === projectSelected,
                        ) as any),
                      },
                    });
                  }}
                ></Button>
              </Tooltip>
              <Tooltip title="Upload File">
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => setIsModalFileUploadVisible(true)}
                  style={{ marginLeft: "2px" }}
                ></Button>
              </Tooltip>
              <Tooltip title="View All Files" placement="bottom">
                <Button
                  icon={<FileOutlined />}
                  style={{ marginLeft: "2px" }}
                  size="large"
                  onClick={() => setFileDrawerVisible(true)}
                ></Button>
              </Tooltip>
            </>
          ),
        ]}
      >
        {alertData.message && <AlertComponent {...alertData} />}
        <CustomerInfo
          idClient={idClient}
          onCreateProject={(fromSelected?: boolean) => {
            if (fromSelected) {
              setModalSelectProject(true);
            } else {
              setProjectDrawerStatus({
                open: true,
                create: true,
                update: false,
                payload: {
                  ...PROJECT_INITIAL_STATE,
                  customers: [customerInfo],
                },
              });
            }
          }}
        />
      </Drawer>
      <DemoModal />
      <DrawerCreateOrUpdateProject
        modalStatus={projectDrawerStatus}
        onSubmit={(values) => {
          if (idClient) {
            updateProject(values, idClient, values._id);
          }
        }}
        handleChangeModalStatus={(payload) => {
          setProjectDrawerStatus((prev) => ({
            ...prev,
            payload: payload || prev.payload,
            create: false,
            update: true,
          }));
        }}
        idClient={idClient}
        handleCreateLocation={(locationName) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newLocation: locationName,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
          });
        }}
        handleCreateSource={(source) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newSource: source,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
            showAlert: false,
          });
        }}
        handleCreateNewProjectStatus={(projectStatusName) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newProjectStatus: projectStatusName,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
            showAlert: false,
          });
        }}
        onCancel={() => {
          setProjectDrawerStatus(MODAL_INITIAL_STATE);
        }}
      />
      <Drawer
        open={modalSelectProject}
        title="Click on the project to select it"
        width={"100vw"}
        footer={
          <Button onClick={() => setModalSelectProject(false)}>Cancel</Button>
        }
        onClose={() => setModalSelectProject(false)}
      >
        <ProjectList
          onSelectOne={(project) =>
            handleEditData({
              endpoint: `project/${idClient}/${project._id}/customer/${customerInfo._id}`,
              newData: {},
              next: () => {
                getCustomerData(customerInfo._id, idClient, () => {});
                setModalSelectProject(false);

                getData("projects-paginated", idClient, undefined, {
                  customer: idCustomer,
                  active: true,
                });
              },
            })
          }
          showDetails={false}
          hideColumns={true}
          onChangePage={(current, pageSize) => {
            getData("projects-paginated", idClient, undefined, {
              customer: idCustomer,
              active: true,
              page: current,
              perPage: pageSize,
            });
          }}
        />
      </Drawer>
      <CustomerFilesDrawer
        idClient={idClient || ""}
        idCustomer={idCustomer || ""}
        open={fileDrawerVisible}
        onClose={() => setFileDrawerVisible(false)}
      />
      <FileUploadModal
        open={isModalFileUploadVisible}
        idClient={idClient}
        idCustomer={customerInfo._id}
        idProject={projectSelected || undefined}
        onClose={() => setIsModalFileUploadVisible(false)}
        onUploadSuccess={() => setFileDrawerVisible(true)}
      />
    </>
  );
};
